import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from './useAuth';

export interface AICredits {
  credits_used: number;
  credits_limit: number;
  credits_remaining: number;
}

export type AIProvider = 'openai' | 'huggingface' | 'auto';

export interface GenerateImageOptions {
  productName: string;
  description?: string;
  productId?: string;
  locationId?: string;
  style?: 'food' | 'product' | 'retail';
  provider?: AIProvider;
}

export interface GenerateImageResult {
  success: boolean;
  imageUrl?: string;
  generationTimeMs?: number;
  credits?: AICredits;
  error?: string;
  upgradeRequired?: boolean;
  provider?: string;
  availableProviders?: string[];
}

export function useAIImageGeneration() {
  const { organization, user } = useAuth();
  const queryClient = useQueryClient();
  const [isGenerating, setIsGenerating] = useState(false);
  const [availableProviders, setAvailableProviders] = useState<string[]>([]);
  const [lastUsedProvider, setLastUsedProvider] = useState<string | null>(null);

  // Fetch current credits
  const {
    data: credits,
    isLoading: creditsLoading,
    refetch: refetchCredits,
    isError: creditsError,
  } = useQuery({
    queryKey: ['ai-credits', organization?.id],
    queryFn: async () => {
      if (!organization?.id) return null;

      try {
        const { data, error } = await supabase
          .rpc('get_ai_credits', { p_organization_id: organization.id });

        if (error) {
          // If the function doesn't exist yet (migration not run), return default credits
          if (error.message?.includes('function') || error.code === '42883') {
            console.warn('AI credits function not found - migration may not be applied yet');
            return {
              credits_used: 0,
              credits_limit: 25,
              credits_remaining: 25,
            };
          }
          console.error('Error fetching AI credits:', error);
          throw error;
        }

        // RPC returns an array, get first item
        return (data?.[0] as AICredits) || {
          credits_used: 0,
          credits_limit: 25,
          credits_remaining: 25,
        };
      } catch (err) {
        // On any error, return default credits to allow the user to try
        // The edge function will do server-side validation anyway
        console.warn('Failed to fetch AI credits, using defaults:', err);
        return {
          credits_used: 0,
          credits_limit: 25,
          credits_remaining: 25,
        };
      }
    },
    enabled: !!organization?.id,
    staleTime: 30000, // 30 seconds
    retry: false, // Don't retry if the function doesn't exist
  });

  // Generate image mutation
  const generateMutation = useMutation({
    mutationFn: async (options: GenerateImageOptions): Promise<GenerateImageResult> => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Not authenticated');
      }

      try {
        // Explicitly pass the Authorization header to ensure it's included
        const response = await supabase.functions.invoke('generate-product-image', {
          body: options,
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        });

        if (response.error) {
          // Handle specific error cases
          if (response.error.message?.includes('401') || response.error.message?.includes('Unauthorized')) {
            throw new Error('Authentication failed. Please refresh and try again.');
          }
          if (response.error.message?.includes('not configured') || response.error.message?.includes('HUGGINGFACE')) {
            throw new Error('AI image generation is not configured. Contact support.');
          }
          throw new Error(response.error.message || 'Failed to generate image');
        }

        return response.data as GenerateImageResult;
      } catch (err: any) {
        // Re-throw with better error message
        if (err.message?.includes('FunctionsHttpError') || err.message?.includes('401')) {
          throw new Error('Unable to connect to AI service. Ensure edge functions are running.');
        }
        throw err;
      }
    },
    onSuccess: (data) => {
      // Update credits cache
      if (data.credits) {
        queryClient.setQueryData(['ai-credits', organization?.id], data.credits);
      }
      // Track available providers and last used provider
      if (data.availableProviders) {
        setAvailableProviders(data.availableProviders);
      }
      if (data.provider) {
        setLastUsedProvider(data.provider);
      }
      // Invalidate products query if a product was updated
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });

  // Generate image with loading state management
  const generateImage = useCallback(async (options: GenerateImageOptions): Promise<GenerateImageResult> => {
    if (!user) {
      return { success: false, error: 'Not authenticated' };
    }

    // Only block if we definitively know credits are exhausted
    // If credits is null/undefined, let the edge function validate
    if (credits && credits.credits_remaining <= 0) {
      return { 
        success: false, 
        error: 'No AI image credits remaining. Upgrade your plan for more credits.',
        upgradeRequired: true,
        credits,
      };
    }

    setIsGenerating(true);
    try {
      const result = await generateMutation.mutateAsync(options);
      return result;
    } catch (error: any) {
      // Check if it's a credits exhausted error
      if (error.message?.includes('No AI image credits') || error.message?.includes('402')) {
        return {
          success: false,
          error: 'No AI image credits remaining. Upgrade your plan for more credits.',
          upgradeRequired: true,
          credits: credits || undefined,
        };
      }
      return {
        success: false,
        error: error.message || 'Failed to generate image',
      };
    } finally {
      setIsGenerating(false);
      // Refetch credits to ensure UI is in sync
      refetchCredits();
    }
  }, [user, credits, generateMutation, refetchCredits]);

  // Calculate credit tier info
  const getTierInfo = useCallback(() => {
    if (!credits) return null;
    
    const limit = credits.credits_limit;
    let tier = 'Starter';
    let nextTier: string | null = 'Professional';
    let nextTierCredits: number | null = 100;

    if (limit >= 500) {
      tier = 'Enterprise';
      nextTier = null;
      nextTierCredits = null;
    } else if (limit >= 100) {
      tier = 'Professional';
      nextTier = 'Enterprise';
      nextTierCredits = 500;
    }

    return {
      tier,
      nextTier,
      nextTierCredits,
      creditsUsedPercent: Math.round((credits.credits_used / credits.credits_limit) * 100),
    };
  }, [credits]);

  return {
    // State
    credits,
    creditsLoading,
    isGenerating,
    availableProviders,
    lastUsedProvider,
    
    // Actions
    generateImage,
    refetchCredits,
    
    // Computed - default to true if credits couldn't be fetched (edge function validates server-side)
    hasCredits: credits ? credits.credits_remaining > 0 : !creditsError,
    tierInfo: getTierInfo(),
    
    // Mutation state
    error: generateMutation.error,
    isError: generateMutation.isError,
  };
}

// Hook for fetching generation history (for analytics/admin)
export function useAIGenerationHistory(options?: { limit?: number }) {
  const { organization } = useAuth();
  const limit = options?.limit || 50;

  return useQuery({
    queryKey: ['ai-generation-history', organization?.id, limit],
    queryFn: async () => {
      if (!organization?.id) return [];

      const { data, error } = await supabase
        .from('ai_generation_logs')
        .select(`
          id,
          product_id,
          prompt,
          model,
          status,
          image_url,
          error_message,
          generation_time_ms,
          created_at,
          user:profiles(first_name, last_name),
          product:products(name)
        `)
        .eq('organization_id', organization.id)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('Error fetching AI generation history:', error);
        throw error;
      }

      return data || [];
    },
    enabled: !!organization?.id,
  });
}

